/**
 * Money → Usage by client — prototype VIEWS.usage + usageTable (docs/prototype.html L2041–2058) with the step-5
 * override at L3345 ("Type in one client" / "Upload usage report", Product reports above the pills, the
 * Reports entered log below the table).
 *
 * Where each number comes from:
 *  - rows and the four tiles: GET /usage/overview (cells already scoped: managers all, a member their own clients)
 *    and GET /products (each product's falling line);
 *  - the 14-day line, source chip, unit and extra numbers: GET /usage/clients/{id} per client in view;
 *  - the health dot: GET /clients (`band`);
 *  - Billed (money.read only): GET /money/usage-vs-billing — the Zoho invoice lines mapped to the product, or the
 *    plan's monthly value while nothing is billed yet (labelled as such, never passed off as billed).
 * Missing data is shown as missing, never as zero use.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { USAGE_SOURCE_LABEL, type ClientProductUsage, type Priority, type UsageOverview, type UsageSource, type UsageVsBillingLine } from '@amplispot/contracts';
import { useClients } from '../../lib/data';
import { inr, useUsageVsBilling } from '../../lib/money';
import { useSession } from '../../lib/session';
import { nf, useClientUsages, useProducts, useUsageOverview } from '../../lib/usage';
import { Empty, ErrorState, Loading, MultiSelectFilter, errorText } from '../../ui/kit';
import { Lbl } from '../../shell/Help';
import { Stat } from '../timesheets/TimesheetReport';
import { LogoWithDot } from '../clients/parts';
import { Spark } from '../usage/parts';
import { ProductReports, ReportsEntered } from '../usage/UsageReports';
import { TypeInModal } from '../usage/TypeInModal';
import { UploadModal } from '../usage/UploadModal';

type Cell = UsageOverview['clients'][number]['cells'][number];
type Row = { client: UsageOverview['clients'][number]['client']; cell: Cell };

const PW: Record<Priority, number> = { high: 3, med: 2, low: 1 };
/** Prototype SOURCES[s].chip. */
const SOURCE_CHIP: Record<UsageSource, string> = { api: 'hot', csv: 'info', manual: '' };
/** How many feeds the attention note names before it counts the rest. */
const NOTE_NAMES = 5;
const isFeedIssue = (c: Cell) => c.feed_state.tone === 'cold' || c.feed_state.tone === 'warm';

export function UsageBilling() {
  const { can } = useSession();
  const money = can('money.read');
  const ov = useUsageOverview();
  const pr = useProducts();
  const cl = useClients();
  const bill = useUsageVsBilling(money);
  const nav = useNavigate();
  // Products to show, any number; empty = all. GET /usage/overview returns every client in one response (not paged).
  const [prod, setProd] = useState<string[]>([]);
  const [upload, setUpload] = useState<{ key?: string } | null>(null);
  const [typeIn, setTypeIn] = useState<{ clientId?: string; productKey?: string } | null>(null);

  const all: Row[] = useMemo(
    () =>
      [...(ov.data?.clients ?? [])]
        .sort((a, b) => PW[b.client.priority] - PW[a.client.priority])
        .flatMap(({ client, cells }) => cells.map((cell) => ({ client, cell }))),
    [ov.data],
  );
  const ids = useMemo(() => [...new Set(all.map((r) => r.client.id))], [all]);
  // ids are every client of GET /usage/overview (every active client the caller sees): one GET /usage/clients.
  const usage = useClientUsages(ids, { allActive: true });
  const products = useMemo(() => (ov.data?.products ?? []).filter((p) => all.some((r) => r.cell.product_key === p.key)), [ov.data, all]);
  const names = useMemo(() => new Map((ov.data?.products ?? []).map((p) => [p.key, p])), [ov.data]);
  const fallOf = useMemo(() => new Map((pr.data ?? []).map((p) => [p.key, p.fall_pct])), [pr.data]);
  const bands = useMemo(() => new Map((cl.data ?? []).map((c) => [c.id, c.band])), [cl.data]);
  const billed = useMemo(() => {
    const m = new Map<string, UsageVsBillingLine>();
    for (const c of bill.data?.data ?? []) for (const p of c.products) m.set(`${c.client.id}|${p.product_key}`, p);
    return m;
  }, [bill.data]);

  if (ov.isPending) return <div className="panel"><Loading label="Loading usage…" /></div>;
  if (ov.isError) return <div className="panel"><ErrorState error={ov.error} onRetry={() => void ov.refetch()} /></div>;

  const fallLine = (k: string) => fallOf.get(k) ?? 15;
  const low = all.filter((r) => r.cell.util != null && r.cell.tone === 'cold').length;
  const falling = all.filter((r) => r.cell.util != null && r.cell.trend != null && r.cell.trend <= -fallLine(r.cell.product_key)).length;
  const issues = all.filter((r) => isFeedIssue(r.cell));
  const rows = all.filter((r) => !prod.length || prod.includes(r.cell.product_key));
  // Clients whose GET /usage/clients/{id} failed: their source, 14-day line and Enter/Upload are unknown, and said so.
  const failedIds = ids.filter((id) => usage.get(id)?.failed);
  const failedNames = failedIds.map((id) => all.find((r) => r.client.id === id)!.client.name);
  const pname = (k: string) => names.get(k)?.name ?? k;

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0, display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => setTypeIn({})} disabled={!all.length}>
            Type in one client
          </button>
          <button className="btn pri" onClick={() => setUpload({})}>
            Upload usage report
          </button>
        </div>
      </div>
      <div className="stats">
        <Stat k="Client-products tracked" v={all.length} />
        <Stat k="Below their low-use line" v={low} s="paying for little" cls="red" />
        <Stat k="Falling vs own usual" v={falling} cls="amber" />
        <Stat k="Data late or missing" v={issues.length} s="not the same as zero use" cls={issues.length ? 'amber' : ''} />
      </div>
      {issues.length ? (
        <div className="note warn" style={{ marginBottom: 14 }}>
          <b>
            {issues.length} feed{issues.length > 1 ? 's' : ''} need attention:
          </b>{' '}
          {issues
            .slice(0, NOTE_NAMES)
            .map((r) => `${r.client.name} ${pname(r.cell.product_key)} (${r.cell.feed_state.label.toLowerCase()})`)
            .join(', ')}
          {issues.length > NOTE_NAMES ? `, and ${issues.length - NOTE_NAMES} more` : ''}.
        </div>
      ) : null}
      {money && bill.data?.freshness.stale && bill.data.freshness.banner ? (
        <div className="note warn" style={{ marginBottom: 14 }}>
          {bill.data.freshness.banner}
        </div>
      ) : null}

      {money && bill.isError ? (
        <div className="note bad" role="alert" style={{ marginBottom: 14 }}>
          <b>Billed amounts could not be loaded,</b> so the Billed column shows a dash. {errorText(bill.error)}{' '}
          <button type="button" className="btn sm" onClick={() => void bill.refetch()} disabled={bill.isFetching}>
            {bill.isFetching ? 'Trying…' : 'Try again'}
          </button>
        </div>
      ) : null}
      {failedNames.length ? (
        <div className="note bad" role="alert" style={{ marginBottom: 14 }}>
          <b>
            Usage details for {failedNames.slice(0, NOTE_NAMES).join(', ')}
            {failedNames.length > NOTE_NAMES ? ` and ${failedNames.length - NOTE_NAMES} more` : ''} could not be loaded,
          </b>{' '}
          so their data source, last 14 days and Enter or Upload are not shown.{' '}
          <button type="button" className="btn sm" onClick={() => failedIds.forEach((id) => usage.get(id)?.retry())}>
            Try again
          </button>
        </div>
      ) : null}

      <ProductReports onUpload={(key) => setUpload({ key })} />

      <div className="bar">
        <MultiSelectFilter
          label="Product"
          allLabel="All products"
          options={products.map((p) => ({ value: p.key, label: p.name, count: all.filter((r) => r.cell.product_key === p.key).length }))}
          value={prod}
          onChange={setProd}
        />
      </div>
      <div className="panel" style={{ overflowX: 'auto' }}>
        {rows.length ? (
          <table className="usig">
            <thead>
              <tr>
                <th>Client</th>
                <th>Product</th>
                <th className="wrap">
                  <Lbl>Used this period, of what they bought</Lbl>
                </th>
                <th>
                  <Lbl>Vs own usual</Lbl>
                </th>
                <th>Last 14 days</th>
                <th>
                  <Lbl>Data</Lbl>
                </th>
                {money ? <th className="tr">Billed</th> : null}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ client: c, cell }) => {
                const u = usage.get(c.id);
                const d = u?.data?.products.find((p) => p.product_key === cell.product_key);
                const meta = names.get(cell.product_key);
                const src = d?.source;
                const urgent = isFeedIssue(cell);
                const open = () => (src === 'csv' ? setUpload({ key: cell.product_key }) : setTypeIn({ clientId: c.id, productKey: cell.product_key }));
                return (
                  <tr key={`${c.id}-${cell.product_key}`} className="row" onClick={() => nav(`/clients/book?client=${encodeURIComponent(c.id)}`)}>
                    <td>
                      <div className="split">
                        <LogoWithDot id={c.id} name={c.name} band={bands.get(c.id) ?? null} />
                        <b>{c.name}</b>
                      </div>
                    </td>
                    <td>
                      {meta?.name ?? cell.product_key}
                      <div className="sub">{meta?.main_metric}</div>
                      {d ? <Extras p={d} /> : null}
                    </td>
                    <td style={{ minWidth: 180 }}>
                      <Used cell={cell} unit={d?.unit} />
                    </td>
                    <td>
                      <Trend cell={cell} fall={fallLine(cell.product_key)} />
                    </td>
                    <td title={`${meta?.main_metric ?? 'Usage'}, last 14 days`}>
                      {d ? (
                        <Spark values={d.days.map((x) => x.util)} est={d.days.map((x) => x.is_estimated)} w={110} h={30} color={cell.trend != null && cell.trend < -5 ? '#E11D48' : '#5B4BE8'} />
                      ) : u?.failed ? (
                        <span className="sub">-</span>
                      ) : (
                        <span className="sub">…</span>
                      )}
                    </td>
                    <td>
                      {src ? (
                        <span className={`chip ${SOURCE_CHIP[src]}`} title={USAGE_SOURCE_LABEL[src].help}>
                          {USAGE_SOURCE_LABEL[src].label}
                        </span>
                      ) : null}
                      <div style={{ marginTop: src ? 4 : 0 }}>
                        <span className={`chip ${cell.feed_state.tone}`}>{cell.feed_state.label}</span>
                      </div>
                    </td>
                    {money ? (
                      <td className="tr num" style={{ whiteSpace: 'nowrap' }}>
                        <Billed line={billed.get(`${c.id}|${cell.product_key}`)} pending={bill.isPending} failed={bill.isError} />
                      </td>
                    ) : null}
                    <td className="tr">
                      {/* Until this client's source is known the action is unknown too (a CSV product must never open
                          Type in): loading → a disabled placeholder; failed → retry that client. */}
                      {src ? (
                        <button
                          className={`btn sm${urgent ? ' pri' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            open();
                          }}
                        >
                          {src === 'csv' ? 'Upload' : 'Enter'}
                        </button>
                      ) : u?.failed ? (
                        <button
                          className="btn sm"
                          title="This client's usage details did not load"
                          onClick={(e) => {
                            e.stopPropagation();
                            u.retry();
                          }}
                        >
                          Try again
                        </button>
                      ) : (
                        <button className="btn sm" disabled aria-busy="true" title="Loading this client's data source…" onClick={(e) => e.stopPropagation()}>
                          …
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Empty title="No products to show">Set up what each client bought in Settings → Products and usage.</Empty>
        )}
      </div>

      <ReportsEntered />

      {upload ? <UploadModal productKey={upload.key} onClose={() => setUpload(null)} /> : null}
      {typeIn ? <TypeInModal clientId={typeIn.clientId} productKey={typeIn.productKey} onClose={() => setTypeIn(null)} /> : null}
    </>
  );
}

/** Prototype extraLine(): the product's other recorded numbers this period. */
function Extras({ p }: { p: ClientProductUsage }) {
  const bits = p.extras.filter((e) => e.value != null);
  if (!bits.length) return null;
  return (
    <div className="sub" style={{ marginTop: 4, display: 'grid', gap: 1, whiteSpace: 'nowrap' }}>
      {bits.map((e) => (
        <span key={e.field_key}>
          {e.label}:{' '}
          <b style={{ color: 'var(--ink)' }}>
            {nf(e.value)}
            {e.unit === '%' ? '%' : ''}
          </b>
        </span>
      ))}
    </div>
  );
}

function Used({ cell, unit }: { cell: Cell; unit?: string }) {
  if (cell.util == null) return <span className={`chip ${cell.feed_state.tone}`}>{cell.feed_state.label}</span>;
  return (
    <>
      <div className="num" style={{ fontSize: 12.5, marginBottom: 4 }}>
        {nf(cell.used)} <span className="sub">of {nf(cell.plan)}{unit ? ` ${unit}` : ''}</span>
      </div>
      <div className="split">
        <div className={`meter grow ${cell.tone}`}>
          <i style={{ width: `${Math.min(100, cell.util)}%` }} />
        </div>
        <span className="num" style={{ fontSize: 12 }}>
          {Math.round(cell.util)}%
        </span>
      </div>
      {cell.util > 100 ? (
        <span className="chip hot" style={{ marginTop: 4 }}>
          Over plan: upsell?
        </span>
      ) : null}
    </>
  );
}

function Trend({ cell, fall }: { cell: Cell; fall: number }) {
  if (cell.trend == null || cell.util == null) return <span className="sub" style={{ color: 'var(--mute)' }}>no data</span>;
  const v = Math.round(cell.trend);
  const cls = cell.trend <= -fall ? 'cold' : v < -5 ? 'warm' : v >= 5 ? 'hot' : '';
  return <span className={`chip ${cls}`}>{v > 0 ? `▲ +${v}` : v < 0 ? `▼ ${v}` : `▬ ${v}`}%</span>;
}

/** What Zoho billed for this product this month; before any invoice line is mapped, the plan's monthly value. */
function Billed({ line, pending, failed }: { line: UsageVsBillingLine | undefined; pending: boolean; failed: boolean }) {
  if (pending) return <span className="sub">…</span>;
  if (failed) return <span className="sub">-</span>;
  if (!line) return <span className="sub">Not set</span>;
  if (line.billed_lakh != null) return <>{inr(line.billed_lakh)}</>;
  if (line.mrr_lakh != null)
    return (
      <span title="Nothing billed by Zoho for this product yet; this is what the plan says they pay a month">
        {inr(line.mrr_lakh)}
        <div className="sub">plan, not billed</div>
      </span>
    );
  return <span className="sub">Not set</span>;
}
